/**
 * Desktop megamenu disclosure semantics.
 *
 * The caret is a real disclosure button — rendered by
 * `snippets/header-megamenu-block.liquid` as
 * `<button type="button" class="x-icon-caret header-caret-toggle">` with a
 * static `aria-expanded="false"`, `aria-controls` on its `#menu-N` panel and an
 * accessible name from the visible menu label. Focus no longer auto-opens a
 * panel; it only applies the `is-focus` styling class. This file binds the
 * buttons, keeps `aria-expanded` truthful, and adds Escape-to-dismiss
 * (WCAG 2.2 SC 1.4.13 — dismissible without moving focus).
 *
 * FB-1563 (Deque 84832): an open panel dims the page like a modal, so while
 * keyboard focus is inside one it IS a modal. The panels carry `role="dialog"`,
 * `aria-modal`, a name and `data-a11y-dialog` statically, and entering one
 * engages the shared trap (`$store.xFocusElement`, assets/custom-a11y-dialog.js),
 * which wraps Tab and marks the rest of the page `inert` + `aria-hidden`.
 * Hover never locks: `inert` on the other nav items would swallow the
 * mouseover that opens them. Instead, keyboard focus landing outside an open,
 * unlocked panel closes it.
 *
 * Behavior lives here rather than in inline Alpine handlers because the markup
 * has 8 near-identical menu-type branches: mirroring state once in JS covers
 * every branch, including merchant blocks added later.
 */
(() => {
  'use strict';

  const OPEN_CLASS = 'is-focus';
  const TOGGLE_SELECTOR = '.header-caret-toggle';
  const PANEL_SELECTOR = '.toggle-menu';
  const BOUND_FLAG = 'a11yMenuBound';

  /** Looked up lazily: vendors.js (Alpine) loads after this file. */
  const menuStore = () =>
    (window.Alpine && window.Alpine.store && window.Alpine.store('xHeaderMenu')) || null;

  const focusStore = () =>
    (window.Alpine && window.Alpine.store && window.Alpine.store('xFocusElement')) || null;

  /** The panel the shared trap currently holds, if we engaged it. */
  let lockedPanel = null;

  /**
   * True only while a pointer press is handing out focus: a click on a link or
   * tab inside a hover-opened panel focuses it, and must not lock the page.
   * Cleared on the next task, after the press's own focus event has run.
   */
  let pointerFocus = false;

  /**
   * Set by a keyboard close, cleared by the first real pointer movement. Lifting
   * the trap's `inert` changes what sits under a resting pointer, and Chrome
   * answers with a synthetic mouseover that would re-open the panel Escape just
   * closed. Movement since then means the user is hovering again.
   */
  let holdHoverReopen = false;

  /**
   * The nav item a caret toggle belongs to. `.has-dropdown` is the hook
   * `theme.js` itself keys off in `selectItem()` / `clickItem()`, and the only
   * class shared by all 8 menu-type branches.
   */
  const itemFor = (toggle) => toggle.closest('.has-dropdown');

  /**
   * ⚠ Panel visibility is NOT the `is-focus` class, which is styling only. The
   * panel is a `.toggle-menu` shown by removing `toggle-menu-hidden`, managed by
   * `xHeaderMenu.selectItem()` / `hideMenu()` in theme.js.
   */
  const isPanelOpen = (panel) => !!panel && !panel.classList.contains('toggle-menu-hidden');

  const isOpen = (item) => isPanelOpen(item && item.querySelector(PANEL_SELECTOR));

  /**
   * Engage the shared trap on an open panel. `focusTarget` keeps focus where the
   * user put it; without one the trap moves focus to the first link.
   */
  const lock = (panel, focusTarget = null) => {
    const store = focusStore();
    if (!store || lockedPanel === panel) return;
    lockedPanel = panel;
    store.trapFocus(panel, focusTarget);
  };

  /**
   * Release the trap once its panel has closed, by whatever path (Escape, the
   * nav's click.away, the header's hover-out). The trap's own observer cannot
   * be relied on here: `toggle-menu-hidden` transitions `visibility`, which
   * still reads `visible` when the class lands, so the page would stay inert.
   * Focus goes back to the caret unless the user has already moved it on.
   */
  const unlock = () => {
    const panel = lockedPanel;
    if (!panel) return;
    lockedPanel = null;

    const active = document.activeElement;
    const strandedFocus = !active || active === document.body || panel.contains(active);
    const item = panel.closest('.has-dropdown');
    const toggle = item && item.querySelector(TOGGLE_SELECTOR);

    const store = focusStore();
    if (store) store.removeTrapFocus(strandedFocus ? toggle : null);
  };

  const closeAll = () => {
    const menu = menuStore();
    if (!menu || typeof menu.hideMenu !== 'function') return;

    holdHoverReopen = true;
    menu.hideMenu(null);

    // Reset the styling state so the caret un-rotates.
    document.querySelectorAll(`.${OPEN_CLASS}`).forEach((el) => {
      el.classList.remove(OPEN_CLASS);
    });
  };

  const syncToggle = (toggle) => {
    const item = itemFor(toggle);
    if (!item) return;
    toggle.setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');
  };

  const syncAll = (root) => {
    root.querySelectorAll(TOGGLE_SELECTOR).forEach(syncToggle);
  };

  /**
   * Open and close through the theme's own paths — `clickItem()` and
   * `hideMenu()`, the latter shared with the nav's `click.away`.
   *
   * `clickItem()` decides direction from the `clicked` class, which the hover
   * path (`selectItem()`) can leave on a different item without clearing.
   * Deciding direction from the panel's real visibility and clearing `clicked`
   * before opening keeps the button honest in both `select_motion` modes.
   */
  const toggleItem = (toggle, event) => {
    const item = itemFor(toggle);
    const menu = menuStore();
    if (!item || !menu) return;

    if (isOpen(item)) {
      menu.hideMenu(item);
    } else {
      item.classList.remove('clicked');
      menu.clickItem(item, event, false, false, false);

      // Enter / Space report `detail === 0`; a mouse click reports the click
      // count. Only a keyboard open moves focus into the panel (and so locks
      // it): a pointer user keeps today's toggle.
      const panel = item.querySelector(PANEL_SELECTOR);
      if (event.detail === 0 && isPanelOpen(panel)) lock(panel);
    }

    syncToggle(toggle);
  };

  const bindToggles = (root) => {
    root.querySelectorAll(TOGGLE_SELECTOR).forEach((toggle) => {
      if (toggle.dataset[BOUND_FLAG]) return;
      toggle.dataset[BOUND_FLAG] = 'true';

      // Bound on the button itself, so `stopPropagation` runs before the
      // wrapper's inline `@click` (select_motion: click) toggles the same panel
      // a second time.
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleItem(toggle, event);
      });
    });
  };

  const initWithin = (root) => {
    bindToggles(root);
    syncAll(root);
  };

  const start = () => {
    const headers = document.querySelectorAll('header, #sticky-header-content');
    if (!headers.length) return;

    headers.forEach((header) => {
      initWithin(header);

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length) {
            // Panels render from <template x-if> / x-teleport, so items and
            // their toggles appear after Alpine init.
            initWithin(header);
          } else if (mutation.type === 'attributes') {
            // A panel gaining or losing `toggle-menu-hidden` is what changes
            // the expanded state. Re-syncing all of a header's toggles is
            // cheaper than working out which item the mutation belongs to,
            // and `attributeFilter` keeps our own aria writes from looping.
            syncAll(header);
          }
        }

        if (lockedPanel && (!lockedPanel.isConnected || !isPanelOpen(lockedPanel))) unlock();
      });

      observer.observe(header, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class']
      });
    });

    /**
     * Escape dismisses an open megamenu without moving focus (WCAG 2.2 SC
     * 1.4.13). Delegates to `xHeaderMenu.hideMenu()`, which hides every panel,
     * clears `.clicked` and updates the backdrop overlay; it ignores its
     * argument unless isSub.
     *
     * Deliberately does not also suppress the theme's inline mouseover
     * handlers — see the known gap in docs/accessibility/remediation.md.
     */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.querySelector('.toggle-menu:not(.toggle-menu-hidden)')) return;

      closeAll();
      headers.forEach(syncAll);
    });

    document.addEventListener('pointerdown', () => {
      pointerFocus = true;
      setTimeout(() => {
        pointerFocus = false;
      }, 0);
    }, true);

    // Capture on window runs before the items' inline Alpine `mouseover`.
    window.addEventListener('mouseover', (event) => {
      if (holdHoverReopen && event.target instanceof Element && event.target.closest('.has-dropdown')) {
        event.stopPropagation();
      }
    }, true);

    window.addEventListener('pointermove', (event) => {
      if (event.movementX || event.movementY) holdHoverReopen = false;
    }, true);

    /**
     * Keyboard focus entering an open panel locks it; landing anywhere else closes
     * an open panel that is not locked (one opened by hover while focus was
     * elsewhere — the state in 84832's screenshot). The item's own link and
     * caret count as inside, so Tab can walk from the trigger into its panel.
     *
     * ⚠ Skips the locked panel: the trap's capture-phase focusin pulls escaping
     * focus back, but this event still arrives with the outside target.
     */
    document.addEventListener('focusin', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || pointerFocus) return;

      const panel = target.closest(PANEL_SELECTOR);
      if (panel && isPanelOpen(panel)) {
        lock(panel, target);
        return;
      }

      const strayPanel = Array.from(document.querySelectorAll(`${PANEL_SELECTOR}:not(.toggle-menu-hidden)`)).find(
        (open) => {
          if (open === lockedPanel) return false;
          const item = open.closest('.has-dropdown');
          return !(item && item.contains(target));
        }
      );

      if (strayPanel) {
        closeAll();
        headers.forEach(syncAll);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
